import { useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { AccessComponentEnum, AccessLevelEnum } from 'graphql-common';
import { ContextProps } from 'hooks/useOutletContext';

interface UseWidgetAccessControlReturn {
  /** Whether interventions are locked at the company plan level */
  isInterventionsLockedByPlan: boolean;
  /** Whether user has access to interventions (user-level permission) */
  isInterventionsAvailable: boolean;
  /** Whether user has access to sites management */
  isSitesAvailable: boolean;
  /** Whether user has access to tasks management */
  isTasksAvailable: boolean;
  /** Whether user has access to assets management */
  isAssetsAvailable: boolean;
  /** Whether user has access to users management */
  isUsersAvailable: boolean;
  /** Check if a specific component is available (not LimitedRead) */
  isComponentAvailable: (component: AccessComponentEnum) => boolean;
  /** Check if a specific component is locked by plan (company-level LimitedRead) */
  isComponentLockedByPlan: (component: AccessComponentEnum) => boolean;
}

/**
 * Hook to manage widget access control logic.
 * Provides access checks for both company-level (plan restrictions) and user-level permissions.
 *
 * Usage:
 * - Company-level (plan): `isInterventionsLockedByPlan` - feature blocked by subscription plan
 * - User-level: `isInterventionsAvailable` - user has permission to access the feature
 *
 * @example
 * const { isInterventionsAvailable, isInterventionsLockedByPlan } = useWidgetAccessControl();
 *
 * if (isInterventionsLockedByPlan) {
 *   return <WidgetNoAccessEmptyState variant="plan" />;
 * }
 * if (!isInterventionsAvailable) {
 *   return <WidgetNoAccessEmptyState />;
 * }
 */
export default function useWidgetAccessControl(): UseWidgetAccessControlReturn {
  const { getUserAccessByComponent, getUserAccessByCompany }: ContextProps =
    useOutletContext();

  // Check company-level access (plan restriction) - only interventions can be locked at company level
  const isInterventionsLockedByPlan = useMemo(
    () =>
      getUserAccessByCompany(AccessComponentEnum.InterventionsManagement) ===
      AccessLevelEnum.LimitedRead,
    [getUserAccessByCompany],
  );

  // Check user-level access permissions for widget kinds
  const isInterventionsAvailable = useMemo(
    () =>
      getUserAccessByComponent(AccessComponentEnum.InterventionsManagement) !==
      AccessLevelEnum.LimitedRead,
    [getUserAccessByComponent],
  );

  const isSitesAvailable = useMemo(
    () =>
      getUserAccessByComponent(AccessComponentEnum.SitesManagement) !==
      AccessLevelEnum.LimitedRead,
    [getUserAccessByComponent],
  );

  const isTasksAvailable = useMemo(
    () =>
      getUserAccessByComponent(AccessComponentEnum.TasksManagement) !==
      AccessLevelEnum.LimitedRead,
    [getUserAccessByComponent],
  );

  const isAssetsAvailable = useMemo(
    () =>
      getUserAccessByComponent(AccessComponentEnum.AssetsManagement) !==
      AccessLevelEnum.LimitedRead,
    [getUserAccessByComponent],
  );

  const isUsersAvailable = useMemo(
    () =>
      getUserAccessByComponent(AccessComponentEnum.UsersManagement) !==
      AccessLevelEnum.LimitedRead,
    [getUserAccessByComponent],
  );

  // Generic function to check if a component is available
  const isComponentAvailable = useMemo(
    () => (component: AccessComponentEnum) =>
      getUserAccessByComponent(component) !== AccessLevelEnum.LimitedRead,
    [getUserAccessByComponent],
  );

  // Generic function to check if a component is locked by plan
  const isComponentLockedByPlan = useMemo(
    () => (component: AccessComponentEnum) =>
      getUserAccessByCompany(component) === AccessLevelEnum.LimitedRead,
    [getUserAccessByCompany],
  );

  return {
    isInterventionsLockedByPlan,
    isInterventionsAvailable,
    isSitesAvailable,
    isTasksAvailable,
    isAssetsAvailable,
    isUsersAvailable,
    isComponentAvailable,
    isComponentLockedByPlan,
  };
}
